import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const ChangePasswordModal = ({ open, onClose }) => {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [cf, setCf] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return null;

  const reset = () => { setCur(''); setNw(''); setCf(''); setErr(''); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (nw.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (nw !== cf) { setErr('Passwords do not match.'); return; }
    if (nw === cur) { setErr('New password must be different from current.'); return; }
    setBusy(true);
    try {
      await ax.post('/api/auth/change-password', { current_password: cur, new_password: nw });
      toast.success('Password updated.');
      close();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not change password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="change-pw-modal" onClick={close}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900"><Lock className="h-4 w-4 text-red-700" /> Change password</div>
          <button type="button" onClick={close} className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="text-[11px] text-gray-500">Use at least 8 characters. If you're a tutor or staff member signing in with a temporary password, please change it now.</div>

        <Field label="Current password" type={show ? 'text' : 'password'} v={cur} on={setCur} testid="pw-current" />
        <Field label="New password" type={show ? 'text' : 'password'} v={nw} on={setNw} testid="pw-new" hint="Min 8 characters" />
        <Field label="Confirm new password" type={show ? 'text' : 'password'} v={cf} on={setCf} testid="pw-confirm" />

        <button type="button" onClick={() => setShow((s) => !s)} className="text-[11px] text-gray-500 flex items-center gap-1 hover:text-gray-700" data-testid="pw-toggle">
          {show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />} {show ? 'Hide' : 'Show'} passwords
        </button>

        {err && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="pw-error">{err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={close} className="h-10 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button type="submit" disabled={busy} className="h-10 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="pw-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Update password
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, type, v, on, testid, hint }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</label>
    <input type={type} required value={v} onChange={(e) => on(e.target.value)}
      className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid={testid} />
    {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
  </div>
);

export default ChangePasswordModal;
